import * as React from 'react';
import Button from '@mui/material/Button';
import {
  Badge,
  Box,
  Divider,
  IconButton,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import {
  COLOR_BLACK_HAZE,
  COLOR_DENIM,
  COLOR_HUMMINGBIRD,
  COLOR_WHITE,
  FONT_SIZE_13_PX,
  FONT_SIZE_16_PX,
  FONT_WEIGHT_SEMI_BOLD,
} from '../../popTheme';
import { defineMessages, useIntl } from 'react-intl';
import SortIcon from '@mui/icons-material/Sort';
import CancelIcon from '@mui/icons-material/Cancel';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

export const FilterMenu = ({ setFilter }) => {
  const theme = useTheme();
  theme.typography.body1 = {
    fontSize: FONT_SIZE_16_PX,
    [theme.breakpoints.only('xs')]: {
      fontSize: FONT_SIZE_13_PX,
    },
  };

  const intl = useIntl();
  const messages = defineMessages({
    filterMenuButtonLabel: {
      id: 'filter.menu.button.label',
      defaultMessage: 'Filter Resources',
      description: 'Filter menu button label',
    },
    filterByMediaTypeLabel: {
      id: 'filterBy.mediaType.label',
      defaultMessage: 'By Media Type',
      description: 'Filter by media type label',
    },
    filterByDurationLabel: {
      id: 'filterBy.duration.label',
      defaultMessage: 'By Duration',
      description: 'Filter by duration label',
    },
    appMediaTypeLabel: {
      id: 'app.media.type.label',
      defaultMessage: 'App',
      description: 'App filter button label',
    },
    moduleMediaTypeLabel: {
      id: 'module.media.type.label',
      defaultMessage: 'Module',
      description: 'Module filter button label',
    },
    podcastMediaTypeLabel: {
      id: 'podcast.media.type.label',
      defaultMessage: 'Podcast',
      description: 'Podcast filter button label',
    },
    videoMediaTypeLabel: {
      id: 'video.media.type.label',
      defaultMessage: 'Video',
      description: 'Video filter button label',
    },
    websiteMediaTypeLabel: {
      id: 'website.media.type.label',
      defaultMessage: 'Website',
      description: 'Website filter button label',
    },
    communityMediaTypeLabel: {
      id: 'community.media.type.label',
      defaultMessage: 'Community',
      description: 'Community filter button label',
    },
    lessThanDurationTypeLabel: {
      id: 'less.than.duration.type.label',
      defaultMessage: 'Less Than 15 Min',
      description: 'Duration less than 15 min button label',
    },
    moreThanDurationTypeLabel: {
      id: 'more.than.duration.type.label',
      defaultMessage: 'More Than 15 Min',
      description: 'Duration more than 15 min button label',
    },
    likedFilterTypeLabel: {
      id: 'liked.filter.type.label',
      defaultMessage: 'Liked',
      description: 'Liked button label',
    },
    completedFilterTypeLabel: {
      id: 'completed.filter.type.label',
      defaultMessage: 'Completed',
      description: 'Completed button label',
    },
    showAllButtonLabel: {
      id: 'show.all.button.label',
      defaultMessage: 'Show All',
      description: 'Show all button label',
    },
  });

  const getButtonWidth = (columnSize) => {
    switch (columnSize) {
      case 3:
        return '6.792rem';
      case 2:
        return '10.563rem';
      default:
        return '21.875rem';
    }
  };

  const getMobileButtonWidth = (columnSize) => {
    switch (columnSize) {
      case 3:
        return '6.15rem';
      case 2:
        return '9.6rem';
      default:
        return '20.125rem';
    }
  };

  const toggleButtonStyle = (columnSize) => {
    return {
      width: getButtonWidth(columnSize),
      height: '2.75rem',
      border: 0,
      lineHeight: 'normal',
      '&.MuiToggleButtonGroup-grouped:not(:first-of-type), &.MuiToggleButtonGroup-grouped:not(:last-of-type), &':
        {
          borderTopRightRadius: '0.375rem',
          borderTopLeftRadius: '0.375rem',
          borderBottomRightRadius: '0.375rem',
          borderBottomLeftRadius: '0.375rem',
        },
      bgcolor: COLOR_HUMMINGBIRD,
      color: COLOR_DENIM,
      fontSize: FONT_SIZE_16_PX,
      fontWeight: FONT_WEIGHT_SEMI_BOLD,
      ':hover': {
        bgcolor: COLOR_HUMMINGBIRD,
      },
      '&.Mui-selected, &.Mui-selected:hover': {
        color: COLOR_WHITE,
        backgroundColor: COLOR_DENIM,
      },
      [theme.breakpoints.down('sm')]: {
        width: getMobileButtonWidth(columnSize),
        height: '2.25rem',
        fontSize: FONT_SIZE_13_PX,
      },
    };
  };

  const sectionLabelTextStyle = {
    mt: '1rem',
    color: COLOR_DENIM,
    fontWeight: FONT_WEIGHT_SEMI_BOLD,
    textAlign: 'center',
  };

  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const [filterParam, setFilterParam] = useState(null);
  const [filterCount, setFilterCount] = useState(0);

  const handleMediaTypeFilter = (event, type) => {
    const newFilterParam = {
      ...filterParam,
      mediaType: type,
    };
    setFilterParam(newFilterParam);
  };

  const handleDurationFilter = (event, duration) => {
    const newFilterParam = {
      ...filterParam,
      duration: duration,
    };
    setFilterParam(newFilterParam);
  };

  const handleActivityFilter = (event, activity) => {
    const newFilterParam = {
      ...filterParam,
      activity: activity,
    };
    setFilterParam(newFilterParam);
  };

  const handleShowAll = () => {
    setFilterParam(null);
  };

  const handleFilterCount = useCallback(() => {
    let result = 0;
    if (filterParam && filterParam.mediaType) {
      result += filterParam.mediaType.length;
    }
    if (filterParam && filterParam.duration) {
      result++;
    }
    if (filterParam && filterParam.activity) {
      result += filterParam.activity.length;
    }
    setFilterCount(result);
  }, [filterParam, setFilterCount]);

  useEffect(() => {
    setFilter(filterParam);
    handleFilterCount();
  }, [filterParam, handleFilterCount, setFilter]);

  return (
    <Box>
      <Badge
        badgeContent={filterCount}
        invisible={filterCount === 0}
        sx={{
          '& .MuiBadge-badge': {
            color: COLOR_WHITE,
            bgcolor: COLOR_DENIM,
          },
        }}
      >
        <Button
          id="filter-menu-button"
          data-testid="filter-menu-button"
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          sx={{
            mb: '1rem',
            width: '18rem',
            fontSize: FONT_SIZE_13_PX,
            fontWeight: FONT_WEIGHT_SEMI_BOLD,
            color: COLOR_DENIM,
            justifyContent: 'flex-start',
            pl: '1.25rem',
            bgcolor: COLOR_HUMMINGBIRD,
            ':hover': {
              bgcolor: COLOR_HUMMINGBIRD,
            },
            [theme.breakpoints.up('sm')]: {
              width: '14.65rem',
              fontSize: FONT_SIZE_16_PX,
            },
          }}
          startIcon={<SortIcon sx={{ color: COLOR_DENIM }} />}
          endIcon={
            <KeyboardArrowDownIcon
              sx={{ [theme.breakpoints.up('sm')]: { display: 'none' }, ml: '6rem' }}
            />
          }
        >
          {intl.formatMessage(messages.filterMenuButtonLabel)}
        </Button>
      </Badge>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        data-testid="filter-menu"
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          backdrop: { invisible: false },
        }}
        sx={{
          [theme.breakpoints.down('sm')]: {
            width: '23.625rem',
          },
        }}
      >
        <Box
          sx={{
            overflowX: 'auto', // enable scrolling on small screens
            maxWidth: { xs: '300px', sm: '400px' },
            whiteSpace: 'nowrap',
            marginRight: { xs: '10px', sm: '0' },
            width: '24.375rem',
            bgcolor: COLOR_BLACK_HAZE,
            borderRadius: '0.375rem',
            [theme.breakpoints.down('sm')]: {
              width: '21.75rem',
            },
          }}
        >
          <SortIcon
            sx={{
              position: { xs: 'relative', sm: 'absolute' },
              top: '0.8rem',
              left: '0.8rem',
              color: COLOR_DENIM,
              [theme.breakpoints.down('sm')]: {
                fontSize: 'large',
                top: { xs: '2rem', sm: '1rem' },
                left: '1rem',
              },
            }}
          />
          <Typography
            variant="body1"
            sx={{
              position: { xs: 'relative', sm: 'none' },
              padding: '1rem',
              ml: '2rem',
              color: COLOR_DENIM,
              fontWeight: FONT_WEIGHT_SEMI_BOLD,
              [theme.breakpoints.down('sm')]: {
                ml: '1.75rem',
              },
            }}
          >
            {intl.formatMessage(messages.filterMenuButtonLabel)}
          </Typography>
          <IconButton
            size="large"
            onClick={handleClose}
            data-testid="close-button"
            sx={{
              position: 'absolute',
              top: { xs: '1.3rem', sm: '0.3rem' },
              right: '0.5rem',
              bgcolor: COLOR_BLACK_HAZE,
              color: COLOR_DENIM,
              ':hover': {
                bgcolor: COLOR_BLACK_HAZE,
              },
            }}
          >
            <CancelIcon sx={{ fontSize: 'large' }} />
          </IconButton>
          <Divider
            variant="middle"
            sx={{ bgcolor: COLOR_DENIM, width: { xs: '19.5rem', sm: '22.3rem' } }}
          />
          <Typography variant="body1" sx={sectionLabelTextStyle}>
            {intl.formatMessage(messages.filterByMediaTypeLabel)}
          </Typography>
          <ToggleButtonGroup
            value={filterParam ? filterParam.mediaType : []}
            onChange={handleMediaTypeFilter}
            aria-label="media type filter"
            data-testid="media-type-toggle-button-group"
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto auto auto',
              gridGap: { xs: '0.4rem', sm: '0.8rem' },
              padding: '1.25rem',
              [theme.breakpoints.down('sm')]: {
                padding: '0.9rem',
              },
            }}
          >
            <ToggleButton
              value={intl.formatMessage(messages.appMediaTypeLabel)}
              aria-label="app"
              data-testid="app-toggle-button"
              sx={toggleButtonStyle(3)}
            >
              {intl.formatMessage(messages.appMediaTypeLabel)}
            </ToggleButton>
            <ToggleButton
              value={intl.formatMessage(messages.moduleMediaTypeLabel)}
              aria-label="module"
              data-testid="module-toggle-button"
              sx={toggleButtonStyle(3)}
            >
              {intl.formatMessage(messages.moduleMediaTypeLabel)}
            </ToggleButton>
            <ToggleButton
              value={intl.formatMessage(messages.podcastMediaTypeLabel)}
              aria-label="podcast"
              data-testid="podcast-toggle-button"
              sx={toggleButtonStyle(3)}
            >
              {intl.formatMessage(messages.podcastMediaTypeLabel)}
            </ToggleButton>
            <ToggleButton
              value={intl.formatMessage(messages.videoMediaTypeLabel)}
              aria-label="video"
              data-testid="video-toggle-button"
              sx={toggleButtonStyle(3)}
            >
              {intl.formatMessage(messages.videoMediaTypeLabel)}
            </ToggleButton>
            <ToggleButton
              value={intl.formatMessage(messages.websiteMediaTypeLabel)}
              aria-label="website"
              data-testid="website-toggle-button"
              sx={toggleButtonStyle(3)}
            >
              {intl.formatMessage(messages.websiteMediaTypeLabel)}
            </ToggleButton>
            <ToggleButton
              value={intl.formatMessage(messages.communityMediaTypeLabel)}
              aria-label="community"
              data-testid="community-toggle-button"
              sx={toggleButtonStyle(3)}
            >
              {intl.formatMessage(messages.communityMediaTypeLabel)}
            </ToggleButton>
          </ToggleButtonGroup>
          <Divider
            variant="middle"
            sx={{ bgcolor: COLOR_DENIM, width: { xs: '19.5rem', sm: '22.3rem' } }}
          />
          <Typography variant="body1" sx={sectionLabelTextStyle}>
            {intl.formatMessage(messages.filterByDurationLabel)}
          </Typography>
          <ToggleButtonGroup
            value={filterParam ? filterParam.duration : ''}
            onChange={handleDurationFilter}
            aria-label="duration filter"
            exclusive
            data-testid="duration-toggle-button-group"
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto auto',
              gridGap: { xs: '0.4rem', sm: '0.8rem' },
              padding: '1.25rem',
              [theme.breakpoints.down('sm')]: {
                padding: '0.9rem',
              },
            }}
          >
            <ToggleButton
              value={intl.formatMessage(messages.lessThanDurationTypeLabel)}
              aria-label="less than 15 min"
              data-testid="less-than-15-min-toggle-button"
              sx={toggleButtonStyle(2)}
            >
              {intl.formatMessage(messages.lessThanDurationTypeLabel)}
            </ToggleButton>
            <ToggleButton
              value={intl.formatMessage(messages.moreThanDurationTypeLabel)}
              aria-label="more than 15 min"
              data-testid="more-than-15-min-toggle-button"
              sx={toggleButtonStyle(2)}
            >
              {intl.formatMessage(messages.moreThanDurationTypeLabel)}
            </ToggleButton>
          </ToggleButtonGroup>
          <Divider
            variant="middle"
            sx={{ bgcolor: COLOR_DENIM, width: { xs: '19.5rem', sm: '22.3rem' } }}
          />
          <ToggleButtonGroup
            value={filterParam ? filterParam.activity : ''}
            onChange={handleActivityFilter}
            aria-label="activity filter"
            data-testid="activity-toggle-button-group"
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto auto',
              gridGap: { xs: '0.4rem', sm: '0.8rem' },
              padding: '1.25rem',
              [theme.breakpoints.down('sm')]: {
                padding: '0.9rem',
              },
            }}
          >
            <ToggleButton
              value={intl.formatMessage(messages.likedFilterTypeLabel)}
              aria-label="liked"
              data-testid="liked-toggle-button"
              sx={toggleButtonStyle(2)}
            >
              {intl.formatMessage(messages.likedFilterTypeLabel)}
            </ToggleButton>
            <ToggleButton
              value={intl.formatMessage(messages.completedFilterTypeLabel)}
              aria-label="completed"
              data-testid="completed-toggle-button"
              sx={toggleButtonStyle(2)}
            >
              {intl.formatMessage(messages.completedFilterTypeLabel)}
            </ToggleButton>
          </ToggleButtonGroup>
          <Divider
            variant="middle"
            sx={{ bgcolor: COLOR_DENIM, width: { xs: '19.5rem', sm: '22.3rem' } }}
          />
          <Box
            component="span"
            margin="1.25rem"
            display="flex"
            alignItems="center"
            sx={{
              [theme.breakpoints.down('sm')]: {
                m: '1rem',
              },
            }}
          >
            <ToggleButton
              value="show all"
              aria-label="show all"
              selected={filterCount === 0}
              onChange={handleShowAll}
              data-testid="show-all-toggle-button"
              sx={toggleButtonStyle(1)}
            >
              {intl.formatMessage(messages.showAllButtonLabel)}
            </ToggleButton>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};
